import React, { useState, useEffect, useRef } from 'react';
import { Bell, Clock, AlertCircle, X, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function NotificationBell() {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState({ today: [], missed: [] });
  const [assignmentNotifications, setAssignmentNotifications] = useState([]);
  const [filter, setFilter] = useState('today'); // 'today', 'missed', or 'assignments'
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    fetchNotifications();
    fetchAssignmentNotifications();
    
    // Poll for notifications every 30 seconds
    const interval = setInterval(() => {
      fetchNotifications();
      fetchAssignmentNotifications();
    }, 30000);
    
    // Close dropdown when clicking outside
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/scheduled-notifications/today`,
        { headers: getAuthHeader() }
      );
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const fetchAssignmentNotifications = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/notifications?limit=20`,
        { headers: getAuthHeader() }
      );
      // Filter for unread assignment notifications
      const unreadAssignments = response.data.filter(n => !n.read);
      setAssignmentNotifications(unreadAssignments);
    } catch (error) {
      console.error('Error fetching assignment notifications:', error);
    }
  };

  const handleTakeAction = (notification) => {
    console.log('🎯 handleTakeAction called:', notification);
    console.log('Lead ID:', notification.lead_id);
    console.log('Notification ID:', notification.id);
    
    // Navigate to lead management and trigger the update modal to open
    // Pass notification_id so it can be marked as complete after update submission
    const url = `/admin/leads?lead_id=${notification.lead_id}&openUpdate=true&notification_id=${notification.id}`;
    console.log('Navigating to:', url);
    
    navigate(url);
    setIsOpen(false);
  };

  const handleSnooze = async (notificationId, e) => {
    e.stopPropagation();
    try {
      await axios.put(
        `${BACKEND_URL}/api/scheduled-notifications/${notificationId}/snooze`,
        {},
        { headers: getAuthHeader() }
      );
      await fetchNotifications();
    } catch (error) {
      console.error('Error snoozing notification:', error);
      alert('Failed to snooze notification');
    }
  };

  const formatTime = (dateStr) => {
    // Parse the UTC time from backend
    const utcDate = new Date(dateStr);
    
    // Convert to IST (add 5.5 hours)
    const istOffset = 5.5 * 60 * 60 * 1000; // 5.5 hours in milliseconds
    const istDate = new Date(utcDate.getTime() + istOffset);
    
    // Format in IST - USE getHours() not getUTCHours()!
    const hours = istDate.getHours(); // FIXED: was getUTCHours()
    const minutes = String(istDate.getMinutes()).padStart(2, '0'); // FIXED: was getUTCMinutes()
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    
    return `${displayHours}:${minutes} ${ampm}`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-IN', { 
        month: 'short', 
        day: 'numeric',
        timeZone: 'Asia/Kolkata'
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'missed':
        return '#ef4444'; // Red
      case 'pending':
        return '#f59e0b'; // Orange
      case 'snoozed':
        return '#a855f7'; // Purple
      default:
        return '#9ca3af'; // Gray
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'missed':
        return 'Overdue';
      case 'pending':
        return 'Due Now';
      case 'snoozed':
        return 'Snoozed';
      default:
        return status;
    }
  };

  const totalCount = notifications.today_count + notifications.missed_count + assignmentNotifications.length;
  const hasMissed = notifications.missed_count > 0;
  const badgeColor = hasMissed ? '#ef4444' : (totalCount > 0 ? '#f59e0b' : '#9ca3af');

  const displayNotifications = filter === 'assignments' 
    ? assignmentNotifications 
    : (filter === 'today' ? notifications.today : notifications.missed);

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Bell Icon */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          border: 'none',
          background: totalCount > 0 ? '#f0f9ff' : 'transparent',
          cursor: 'pointer',
          transition: 'background 0.2s'
        }}
        title={`${totalCount} notifications`}
      >
        <Bell 
          size={20} 
          color={totalCount > 0 ? badgeColor : '#6b7280'}
          strokeWidth={2}
        />
        {totalCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            background: badgeColor,
            color: 'white',
            fontSize: '10px',
            fontWeight: '700',
            borderRadius: '10px',
            padding: '2px 6px',
            minWidth: '18px',
            textAlign: 'center',
            lineHeight: '1'
          }}>
            {totalCount > 99 ? '99+' : totalCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '50px',
          right: '0',
          width: '380px',
          maxHeight: '500px',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          zIndex: 9999,
          overflow: 'hidden',
          border: '1px solid #e5e7eb'
        }}>
          {/* Header */}
          <div style={{
            padding: '16px',
            borderBottom: '1px solid #e5e7eb',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                Follow-Up Notifications
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  width: '28px',
                  height: '28px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                <X size={16} />
              </button>
            </div>
            
            {/* Filter Tabs */}
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                onClick={() => setFilter('today')}
                style={{
                  flex: 1,
                  padding: '7px',
                  background: filter === 'today' ? 'white' : 'rgba(255,255,255,0.2)',
                  color: filter === 'today' ? '#667eea' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '12px',
                  transition: 'all 0.2s'
                }}
              >
                Today ({notifications.today_count})
              </button>
              <button
                onClick={() => setFilter('missed')}
                style={{
                  flex: 1,
                  padding: '7px',
                  background: filter === 'missed' ? 'white' : 'rgba(255,255,255,0.2)',
                  color: filter === 'missed' ? '#ef4444' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '12px',
                  transition: 'all 0.2s'
                }}
              >
                Missed ({notifications.missed_count})
              </button>
              <button
                onClick={() => setFilter('assignments')}
                style={{
                  flex: 1,
                  padding: '7px',
                  background: filter === 'assignments' ? 'white' : 'rgba(255,255,255,0.2)',
                  color: filter === 'assignments' ? '#8b5cf6' : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '12px',
                  transition: 'all 0.2s'
                }}
              >
                Assigned ({assignmentNotifications.length})
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '8px'
          }}>
            {displayNotifications.length === 0 ? (
              <div style={{
                padding: '40px 20px',
                textAlign: 'center',
                color: '#9ca3af'
              }}>
                <Bell size={48} style={{ margin: '0 auto 16px', opacity: 0.3 }} />
                <p style={{ margin: 0, fontSize: '14px' }}>
                  {filter === 'today' 
                    ? 'No follow-ups scheduled for today' 
                    : filter === 'missed'
                    ? 'No missed follow-ups'
                    : 'No new assignments'}
                </p>
              </div>
            ) : filter === 'assignments' ? (
              // Display assignment notifications
              displayNotifications.slice(0, 11).map((notif) => {
                // Get lead_id from related_to_id if related_to_type is 'lead'
                const leadId = notif.related_to_type === 'lead' ? notif.related_to_id : notif.lead_id;
                
                return (
                <div
                  key={notif.id}
                  onClick={() => {
                    // Mark as read and navigate to lead
                    axios.put(
                      `${BACKEND_URL}/api/notifications/${notif.id}/mark-read`,
                      {},
                      { headers: getAuthHeader() }
                    );
                    if (leadId) {
                      navigate(`/admin/leads?lead_id=${leadId}&openUpdate=true`);
                    }
                    setIsOpen(false);
                  }}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    background: notif.read ? '#f9fafb' : 'white',
                    border: `2px solid #8b5cf6`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = notif.read ? '#f9fafb' : 'white';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div style={{
                    fontWeight: '600',
                    fontSize: '13px',
                    color: '#1f2937',
                    marginBottom: '6px'
                  }}>
                    {notif.title || 'New Assignment'}
                  </div>
                  <div style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    lineHeight: '1.4'
                  }}>
                    {notif.message}
                  </div>
                  {leadId && (
                    <div style={{
                      marginTop: '8px',
                      padding: '6px',
                      background: '#f3f4f6',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '600',
                      color: '#6b7280'
                    }}>
                      Lead: {leadId}
                    </div>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (leadId) {
                        navigate(`/admin/leads?lead_id=${leadId}&openUpdate=true`);
                      }
                      setIsOpen(false);
                    }}
                    style={{
                      width: '100%',
                      marginTop: '8px',
                      padding: '6px',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}
                  >
                    Take Action
                  </button>
                </div>
              );
              })
            ) : (
              // Display follow-up notifications (existing code)
              displayNotifications.slice(0, 11).map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => handleTakeAction(notif)}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    background: 'white',
                    border: `2px solid ${getStatusColor(notif.status)}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  {/* Status Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    background: getStatusColor(notif.status),
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: '700',
                    padding: '3px 8px',
                    borderRadius: '4px'
                  }}>
                    {getStatusLabel(notif.status)}
                  </div>

                  {/* Lead ID and Name */}
                  <div style={{
                    fontWeight: '600',
                    fontSize: '13px',
                    color: '#1f2937',
                    marginBottom: '4px',
                    paddingRight: '70px'
                  }}>
                    {notif.lead_id} - {notif.lead_name}
                  </div>

                  {/* Time */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '11px',
                    color: '#374151',
                    marginBottom: '6px'
                  }}>
                    <Clock size={12} style={{ color: getStatusColor(notif.status) }} />
                    <span style={{ fontWeight: '600' }}>
                      {formatDate(notif.scheduled_time)} at {formatTime(notif.scheduled_time)}
                    </span>
                  </div>

                  {/* Remarks Preview */}
                  <div style={{
                    fontSize: '11px',
                    color: '#6b7280',
                    marginBottom: '8px',
                    fontStyle: 'italic',
                    lineHeight: '1.3',
                    maxHeight: '28px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}>
                    "{notif.remarks}"
                  </div>

                  {/* Action Button */}
                  <div style={{
                    display: 'flex',
                    marginTop: '8px'
                  }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTakeAction(notif);
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px'
                      }}
                    >
                      <ExternalLink size={12} />
                      Take Action
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
